/**
 * Banner 11 — Middle Banner (ASU-010).
 *
 * Figma (Atlas Website, file PdDGTiH65FKjDxz4cIVllK):
 *   1750 12168:236 · 1430 12165:3 · 1230 12166:133 · 1080 12168:190 · mobile 12169:292
 *
 * A full-bleed background image (separate desktop / mobile uploads) holding a
 * left square image (photo + overlay text/logo baked into the upload) beside a
 * content column: heading (1 line desktop / 2 mobile), paragraph (3 lines
 * desktop / 5 mobile), 1–3 tick bullets and two CTAs — "Apply Now" (solid,
 * opens the lead form) + "Prospectus" (outline, opens the brochure-download
 * lead drawer). Neither button takes a link.
 *
 * Column reuse: heading → title_1, paragraph → paragraph_text, bullets →
 * metadata, primary CTA → download_brochure_text / button_color /
 * cta_text_color, secondary CTA → apply_now_text / apply_now_color /
 * apply_now_border_color. Only new column: left_image.
 */
const IMAGE_ACCEPT = "image/png,image/jpeg,image/webp,image/gif,image/svg+xml";

export const BANNER_11_CONSTANTS = {
  METADATA_TITLES: {
    metadata: "Bullet points (min 1, max 3)",
  },

  // Per-group row-count limits enforced in the admin table editor.
  METADATA_LIMITS: {
    metadata: { min: 1, max: 3 },
  },

  // Bullet row
  DEFAULT_ROW: {
    id: "1",
    text: "Flexible learning with AI",
    text_color: "#081F3D",
    icon: "",
    priority: 1,
    status: true,
  },

  BANNER_FIELDS_CONFIG: [
    // ── Widget background (separate desktop / mobile uploads) ─────────────
    {
      fieldName: "background_image",
      label: "Widget background image (desktop / tablet)",
      value: "",
      valueType: "file",
      filedType: "FILE",
      placeholder: "Upload full-bleed background (decorative shapes baked in)",
      defaultValue: "",
      accept: IMAGE_ACCEPT,
    },
    {
      fieldName: "background_image_mobile",
      label: "Widget background image (mobile)",
      value: "",
      valueType: "file",
      filedType: "FILE",
      placeholder: "Separate background for mobile — falls back to desktop / colour",
      defaultValue: "",
      accept: IMAGE_ACCEPT,
    },
    {
      fieldName: "background_color",
      label: "Background colour (fallback when no image)",
      value: "#D6DE66",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#D6DE66",
    },

    // ── Left image (square; photo + overlay baked into the upload) ────────
    {
      fieldName: "left_image",
      label: "Left image (square — photo + overlay baked into the image)",
      value: "",
      valueType: "file",
      filedType: "FILE",
      placeholder: "Upload the left card image",
      defaultValue: "",
      accept: IMAGE_ACCEPT,
    },

    // ── Heading + paragraph ──────────────────────────────────────────────
    {
      fieldName: "title_1",
      label: "Heading (mandatory — 1 line desktop / 2 lines mobile)",
      value: "Admissions Open for 2026",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Admissions Open for 2026",
      required: true,
      defaultValue: "Admissions Open for 2026",
    },
    {
      fieldName: "title_1_color",
      label: "Heading colour",
      value: "#081F3D",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#081F3D",
    },
    {
      fieldName: "paragraph_text",
      label: "Paragraph (max 3 lines desktop / 5 lines mobile)",
      value:
        "Join a new generation of business professionals equipped to navigate change, leverage AI, and create impact.",
      valueType: "string",
      filedType: "TEXT_AREA",
      placeholder: "Enter supporting paragraph",
      defaultValue:
        "Join a new generation of business professionals equipped to navigate change, leverage AI, and create impact.",
    },
    {
      fieldName: "paragraph_color",
      label: "Paragraph text colour",
      value: "#081F3D",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#081F3D",
    },
    {
      fieldName: "bullet_icon",
      label: "Default bullet icon (optional — defaults to green tick-circle)",
      value: "",
      valueType: "file",
      filedType: "FILE",
      placeholder: "Upload a shared bullet icon",
      defaultValue: "",
      accept: IMAGE_ACCEPT,
    },

    // ── Primary CTA ("Apply Now" — solid; opens the lead form) ───────────
    {
      fieldName: "download_brochure_text",
      label: "Primary button label (opens the lead form; empty to hide)",
      value: "Apply Now",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Apply Now",
      defaultValue: "Apply Now",
    },
    {
      fieldName: "button_color",
      label: "Primary button background colour",
      value: "#081F3D",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#081F3D",
    },
    {
      fieldName: "cta_text_color",
      label: "Primary button text colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFFFFF",
    },

    // ── Secondary CTA ("Prospectus" — outline; brochure-download form) ────
    {
      fieldName: "apply_now_text",
      label: "Secondary button label (opens the brochure form; empty to hide)",
      value: "Prospectus",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Prospectus",
      defaultValue: "Prospectus",
    },
    {
      fieldName: "apply_now_color",
      label: "Secondary button text colour",
      value: "#081F3D",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#081F3D",
    },
    {
      fieldName: "apply_now_border_color",
      label: "Secondary button border colour",
      value: "#081F3D",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#081F3D",
    },

    // ── Flags ────────────────────────────────────────────────────────────
    {
      fieldName: "enable_animation",
      label: "Enable scroll-reveal animation",
      value: false,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: false,
    },
    {
      fieldName: "status",
      label: "Banner Status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
  ],

  // metadata — bullet points (1–3)
  METADATA_FIELDS: [
    {
      header: "Bullet text",
      fieldName: "text",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Flexible learning with AI",
      defaultValue: "",
    },
    {
      header: "Bullet text colour",
      fieldName: "text_color",
      value: "#081F3D",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "e.g. #081F3D",
      defaultValue: "#081F3D",
    },
    {
      header: "Bullet icon (optional — overrides the default tick)",
      fieldName: "icon",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload bullet icon",
      defaultValue: "",
      accept: IMAGE_ACCEPT,
    },
    {
      header: "Priority",
      fieldName: "priority",
      value: 1,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "Display order (lower first)",
      defaultValue: 1,
    },
    {
      header: "Status",
      fieldName: "status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Enable this bullet",
      defaultValue: true,
    },
  ],
};
